// BRIEF 95 §3a(a) — A DATE FIELD THAT SHOWS DD/MM/YY AND STORES ISO.
//
// ── WHAT WAS ON THE SCREEN ──────────────────────────────────────────────────
//
// A task row and its open drawers disagreed about what a date is. The row's own
// due date renders through `fd()` — `toLocaleDateString('en-GB')`, so
// `12/06/2026` — while the two `<input type="date">` fields a few pixels below
// it rendered `06/18/2026` and `mm/dd/yyyy`. Two orders, one screen. And the
// subtask date column, sized `calc(10ch + 6px)` for DD/MM/YYYY, was ALREADY
// clipping its own value: measured in Chromium at the row's 11px font, it shows
// `06/18/202`.
//
// ── WHY IT IS AN OVERLAY AND NOT AN ATTRIBUTE ───────────────────────────────
//
// `<input type="date">` takes its display format from the BROWSER's locale and
// nothing else. Tested rather than remembered, in the same Chromium the app
// runs in: `lang="en-GB"` and `lang="fr-BE"` both still render `06/18/2026`.
// There is no attribute, property or CSS that reformats it — and a two-digit
// year is not on offer in any locale.
//
// So the native text is hidden while the control is at rest and our own text is
// drawn over it. The input is UNCHANGED underneath: same `type="date"`, same
// native picker, same `showPicker()`, same ISO `value`, same `onChange`. THE
// DISPLAY FORMAT NEVER REACHES THE WRITE PATH — `onChange` hands out
// `e.target.value`, which is the browser's ISO string, and `dmy2` is a pure
// read-side formatter with no inverse. §3a(a) asks for exactly that boundary.
//
// FOCUS PUTS THE NATIVE EDITOR BACK. Hiding the digits somebody is typing would
// be worse than the format it fixes, so `:focus` restores the native editor and
// takes the overlay away — the resting state is DD/MM/YY, the editing state is
// whatever the browser gives, and no keystroke is ever invisible.
//
// ── AND IT DEGRADES ─────────────────────────────────────────────────────────
//
// `::-webkit-datetime-edit` is a Blink/WebKit pseudo-element. In a browser
// without it the rule would be dropped and the overlay would draw a SECOND date
// on top of the native one, so the whole thing sits behind
// `@supports selector(::-webkit-datetime-edit)` (App.css) — the overlay is
// `display: none` until the browser proves it can hide what is underneath.
// A browser that cannot then renders exactly what it renders today.

import type { CSSProperties, FocusEvent, KeyboardEvent, MouseEvent } from "react";
/* The empty state is a WORD, not a mask: it is the format a Belgian reader is
   being asked for, so it is keyed like every other visible string —
   `JJ/MM/AA` in French, `DD/MM/JJ` in Dutch. The filled state is not keyed and
   must not be: those are the record's own digits. */
import { useTranslation } from "../i18n";

/**
 * ISO (`yyyy-mm-dd`, or any longer ISO string) → `DD/MM/YY`. Read-side only:
 * there is deliberately no parser going the other way, because the only writer
 * is the native control and it writes ISO.
 */
export const dmy2 = (iso?: string | null): string => {
  const s = String(iso ?? "").slice(0, 10);
  return /^\d{4}-\d{2}-\d{2}$/.test(s)
    ? `${s.slice(8, 10)}/${s.slice(5, 7)}/${s.slice(2, 4)}`
    : "";
};

type DateFieldProps = {
  /** ISO. Anything that is not `yyyy-mm-dd` reads as empty, as the input does. */
  value?: string | null;
  /** Always the ISO value the browser produced — never the displayed text. */
  onChange: (iso: string) => void;
  /** Paint (padding, font size, background). Defaults to `field-input`. */
  className?: string;
  style?: CSSProperties;
  /** Layout (width, flex, margin) — the wrapper is the flex item now. */
  wrapStyle?: CSSProperties;
  autoFocus?: boolean;
  onBlur?: (e: FocusEvent<HTMLInputElement>) => void;
  onKeyDown?: (e: KeyboardEvent<HTMLInputElement>) => void;
  onClick?: (e: MouseEvent<HTMLInputElement>) => void;
  title?: string;
  /** 208 §10 — the rates panel's rows are read-only to a reader who is not an admin (rule 4: widened, never bent). */
  disabled?: boolean;
};

export default function DateField({
  value,
  onChange,
  className = "field-input",
  style,
  wrapStyle,
  ...rest
}: DateFieldProps) {
  const { t } = useTranslation();
  const shown = dmy2(value);
  return (
    <span
      className="dmyWrap"
      /* The font size rides the wrapper too: the overlay is absolutely
         positioned inside it and would otherwise inherit the page's. */
      style={{ fontSize: style?.fontSize, ...wrapStyle }}
    >
      <input
        {...rest}
        type="date"
        className={`${className} dmyDate`}
        style={style}
        value={String(value ?? "").slice(0, 10)}
        onChange={(e) => onChange(e.target.value)}
      />
      <span className={shown ? "dmyOv" : "dmyOv dmyOvEmpty"} aria-hidden="true">
        {shown || t("date_field.dd_mm_yy", { fallback: "DD/MM/YY" })}
      </span>
    </span>
  );
}
